import { abtastyService, cookieService } from '@/services';
import { trackError } from '@utils/error';
import axios from 'axios';
import { addToCart } from '@/actions/cart.actions';
import iconBundle from '@features/pdp/images/icons/icon-bundle.svg';
import { CartItemAddActionTypes } from '@/constants/cart.constants';
import { setView } from '../../../actions/pdp.actions';
import { useAppDispatch } from '@/store';
import { Button, LoadingSpinner } from '@components/tailwind';

import { formatCurrency } from '@utils/formatters.util';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useMiyoFee from '@/hooks/useMiyoFee';
import DesignExporter from '@/features/partner/components/DesignExporter';
import CookieNames from '@/constants/cookie-names.constants';
import { WebImage } from '@components/tailwind/WebImage';
import { FiArrowRight } from 'react-icons/fi';
import cx from '@utils/cx.util';
import ErrorBoundary from '@components/tailwind/ErrorBoundary';
import { PDPViews } from '@/types/pdp';
import useVariation from '@hooks/useVariation';

type Props = {
  id: string;
  webImages: any;
  rate: number;
  displayName: string;
  typeName: string;
  stock: string;
  isGenericProduct: boolean;
  showPicker: boolean;
  klaviyoConfigured: boolean;
  addOns: any[];
  bundleInfo?: any;
  siteId: string;
  isInlineBundle?: boolean;
};

const PersistentAddToCart = ({
  id,
  webImages,
  rate: itemRate,
  displayName,
  typeName,
  stock,
  isGenericProduct,
  showPicker,
  klaviyoConfigured,
  addOns,
  bundleInfo,
  siteId,
  isInlineBundle = false,
}: Props) => {
  const isVariant = useVariation('Persistent Add to Cart');
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const checkout = document.querySelector('#addToCart');
    const observer = new IntersectionObserver((entries) => {
      setShown(!entries[0].isIntersecting && entries[0].boundingClientRect.y < 0);
    });
    observer.observe(checkout);

    abtastyService.trackClick('Persistent Add to Cart: Render');

    const modalObserver = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const addedToCartModal = document.querySelector('#AddedToCartModal');
          if (addedToCartModal) {
            setShown(false);
          } else {
            const addToCart = document.querySelector('#addToCart');
            if (addToCart) {
              setShown(window.scrollY > addToCart.getBoundingClientRect().top);
            }
          }
        }
      }
    });

    modalObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      modalObserver.disconnect();
    };
  }, []);

  const dispatch = useAppDispatch();
  const { loading } = useSelector((state: any) => state.cartItemAdd);
  const {
    upsell: { selected },
    shopifyId,
    miyo,
    canMiyoProduct: _canMiyoProduct,
    details,
  } = useSelector((state: any) => state.pdpItem);

  const miyoOverride = details?.uom.id === 1;
  const canMiyoProduct = miyoOverride && _canMiyoProduct;

  const miyoFee = useMiyoFee();

  const rate = itemRate + miyoFee + (addOns || []).reduce((acc, curr) => acc + curr.rateEach, 0);

  const isBundle = isInlineBundle;
  const primaryItem = bundleInfo?.selectedItems[0] || {};

  const title = isBundle ? primaryItem.displayName : typeName;

  const designExporterRef = useRef<DesignExporter>();

  const checkShopifyId = useMemo(() => cookieService.get(CookieNames.SHOPIFY_ENABLED) === 'true', []);

  const _addToCartSubmit = async (design = null) => {
    const { id: designId, graphicType, printType } = design || {};

    abtastyService.trackClick('Persistent Add to Cart: Add to Cart');

    const cartItems = {
      item: {
        id,
        quantity: 1,
        rateFormatted: formatCurrency(rate),
        rate,
        displayName,
        webImages,
        isBundle,
        isInlineBundle,
        isUpsell: false,
        designId,
        graphicType,
        printType,
      },
      upsell: null,
      bundleItems: null,
      addOns: null,
    };

    if (selected.length > 0) {
      cartItems.upsell = selected.map((i) => {
        return {
          baseShopItemId: id,
          id: i.itemId,
          quantity: 1,
          rateFormatted: formatCurrency(i.rate),
          rate: i.rate,
          displayName: i.item.displayName,
          webImages: i.item.webImages,
          isUpsell: true,
          isBundle: false,
          upsellDetails: i,
        };
      });
    }

    if (isBundle) {
      const designs = await Promise.all(
        bundleInfo.customInfo.designs.map(async (data) => {
          if (data?.formData) {
            const { data: designId } = await axios.post('/custom/design/update', data.formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
            return {
              designId,
              graphicType: data.designData.graphicType,
              printType: data.designData.printType,
            };
          } else {
            return {};
          }
        })
      );
      bundleInfo.customInfo.clear();

      cartItems.bundleItems = bundleInfo.selectedItems.map((item, i) => {
        return {
          id: item.id,
          itemId: item.id,
          src: item.webImages.default.thumb.url,
          alt: item.webImages.default.thumb.altText ?? item.displayName,
          ...designs[i],
        };
      });
    }

    if (addOns.length > 0) {
      cartItems.addOns = addOns.map((i) => {
        return {
          id: i.id,
          quantity: cartItems.item.quantity,
          rateFormatted: formatCurrency(i.rateEach),
          displayName: i.displayName,
          webImages: i.webImages,
          isUpsell: false,
          isBundle: false,
          rate: i.rate,
        };
      });
    }

    dispatch(addToCart(cartItems, siteId));
  };

  const addToCartSubmit = () => {
    dispatch({
      type: CartItemAddActionTypes.REQUEST,
    });
    !!designExporterRef.current ? designExporterRef.current.submit() : _addToCartSubmit();
  };

  const renderCTABtn = useCallback(() => {
    if (isBundle) {
      return (
        <Button
          type='button'
          onClick={() => {
            const productMain = document.querySelector('.product-main');
            productMain?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            abtastyService.trackClick('Persistent Add to Cart: See Options');
          }}
          fullWidth='all'
          size='base'>
          Build Your Bundle
        </Button>
      );
    }

    if (loading) {
      return (
        <Button type='button' fullWidth='all' size='base' disabled>
          <LoadingSpinner bgVariant='none' inline white />
          <span className='tw:ml-2'>Adding to cart</span>
        </Button>
      );
    } else if (stock === 'soldOut') {
      <meta itemProp='availability' content='https://schema.org/OutOfStock' />;
      if (klaviyoConfigured && !isBundle) {
        return (
          <Button
            type='button'
            variant='secondary'
            size='base'
            data-bs-toggle='modal'
            data-bs-target='#backInStockSubscribe'
            data-item-id={id}
            data-item-display-name={displayName}>
            Notify Me When In Stock
          </Button>
        );
      } else {
        return (
          <Button disabled variant='primary' size='base'>
            SOLD OUT
          </Button>
        );
      }
    } else if (isGenericProduct && showPicker) {
      return (
        <Button type='button' fullWidth='all' size='base' disabled>
          Make a selection to purchase
        </Button>
      );
    } else if (stock === 'preOrder') {
      return (
        <React.Fragment>
          <meta itemProp='availability' content='https://schema.org/PreOrder' />
          <Button type='button' fullWidth='all' variant='primary' size='base' onClick={addToCartSubmit}>
            Pre-Order
          </Button>
        </React.Fragment>
      );
    } else if (checkShopifyId && !shopifyId) {
      trackError('Shopify product not synced', { displayName, id });
      return (
        <Button disabled variant='primary' size='base'>
          TEMPORARILY UNAVAILABLE
        </Button>
      );
    } else {
      return (
        <React.Fragment>
          <meta itemProp='availability' content='https://schema.org/InStock' />
          <Button onClick={addToCartSubmit} fullWidth='all' type='button' variant='primary' size='base'>
            <div>
              {formatCurrency(rate)} - Add to cart
              {selected?.length > 0 && (
                <span className='tw:block tw:text-sm tw:font-light tw:normal-case'>
                  Item + {selected.length} {selected.length > 1 ? <>Accessories</> : <>Accessory</>}
                </span>
              )}
            </div>
          </Button>
        </React.Fragment>
      );
    }
  }, [
    stock,
    isGenericProduct,
    showPicker,
    selected,
    addToCartSubmit,
    klaviyoConfigured,
    isBundle,
    id,
    displayName,
    loading,
  ]);

  const { canEngrave } = useMemo(() => {
    const config = miyo?.preview?.[0]?.miyoConfig?.graphicType || {};

    const graphicTypes = Object.keys(config).filter(
      (key) => window.DD_ENV === 'dev' || config[key] === 'prod'
    );

    const canEngrave = graphicTypes.includes('engraved');
    return {
      canEngrave,
    };
  }, [miyo]);

  const onPersonalizeClick = () => {
    dispatch(setView(PDPViews.DESIGN));
    abtastyService.trackClick('Persistent Add to Cart: MyRTIC');
  };

  const bundleInfoText = useMemo(() => {
    if (!bundleInfo) return null;

    const subheader = bundleInfo.bundle.subheader?.replaceAll(
      '{freeItemsValue}',
      bundleInfo.bundlePricing.freeItemsValue
    );

    return {
      subheader,
    };
  }, [bundleInfo]);

  if (!isVariant) return null;

  return (
    <div
      className={cx(
        'tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:z-[999998] tw:animate-[fadeInDown_0.5s_ease-in-out] tw:bg-white tw:px-4 tw:py-4 tw:text-center tw:shadow-[0px_-3px_15px_rgba(0,0,0,0.2)]',
        {
          'tw:hidden': !shown,
        }
      )}>
      {shown && isVariant && (
        <style>
          {`
            #launcher {
              bottom: 116px !important;
            }

            iframe[title="Message from company"], iframe[title="Close message"] {
              margin-bottom: 100px !important;
            }
        `}
        </style>
      )}
      <div className='tw:mx-auto tw:max-w-[1440px]'>
        <div className='tw:flex tw:flex-col tw:items-center tw:justify-between tw:gap-4 tw:md:flex-row'>
          <div className='tw:flex tw:flex-col tw:gap-4 tw:md:flex-row tw:md:items-center tw:md:gap-9'>
            <Item
              image={isBundle ? primaryItem.webImages.default.thumb : webImages.default.thumb}
              displayName={title}
              size={details.attributes.size}
              color={details.attributes.color}
              compact={isBundle}
            />

            {bundleInfoText && (
              <ErrorBoundary silent>
                <div className='tw:flex tw:items-center tw:gap-3 tw:[&_*]:text-left'>
                  <div className='tw:w-8 tw:shrink-0 tw:grow-0'>
                    <img src={iconBundle} alt='Bundle Icon' className='tw:h-full tw:w-full tw:min-w-8' />
                  </div>
                  <div className='tw:text-sm tw:font-bold tw:leading-tight tw:text-body tw:[&_span]:block tw:[&_span]:text-sm tw:[&_span]:font-medium tw:[&_span]:text-neutrals-medium-grey'>
                    {bundleInfoText.subheader && (
                      <div dangerouslySetInnerHTML={{ __html: bundleInfoText.subheader }} />
                    )}
                  </div>
                </div>
              </ErrorBoundary>
            )}
          </div>

          <div className='tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-2 tw:sm:flex-row tw:md:w-auto'>
            {canMiyoProduct && !isBundle && (
              <Button
                variant='bordered'
                size='base'
                className='tw:w-full tw:md:w-auto'
                onClick={onPersonalizeClick}>
                <span className='tw:flex tw:items-center tw:justify-between tw:gap-2'>
                  <span className='tw:inline-flex tw:items-center tw:gap-1'>
                    <span>{canEngrave ? 'Engrave' : 'Personalize'} with</span>{' '}
                    <img
                      src='/assets/images/rtic/myRTIC-Logo.png'
                      alt='MyRTIC'
                      className='tw:h-[14px] tw:w-full tw:max-w-[65px]'
                    />
                  </span>
                  <FiArrowRight className='tw:h-4 tw:w-4' />
                </span>
              </Button>
            )}
            <div className='tw:w-full tw:md:w-auto'>{renderCTABtn()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersistentAddToCart;

const Item = ({
  image,
  displayName,
  size = undefined,
  color = undefined,
  compact = false,
  rate = undefined,
}) => {
  return (
    <div
      className={cx('tw:flex tw:items-center tw:gap-2 tw:md:flex-row', {
        'tw:md:max-w-[220px]': compact,
      })}>
      <div>
        <WebImage webImage={image} alt={displayName} className='tw:max-w-[65px]' />
      </div>
      <div className='tw:text-left'>
        <p
          className={cx('tw:mb-0 tw:text-base tw:font-bold tw:!leading-none tw:text-body', {
            'tw:text-sm': compact,
          })}>
          {displayName}
        </p>
        {rate && (
          <p
            className={cx('tw:mb-0 tw:text-sm tw:font-medium tw:!leading-none tw:text-neutrals-medium-grey', {
              'tw:mt-1 tw:text-xs': compact,
            })}>
            (A {formatCurrency(rate)} Value)
          </p>
        )}
        {(size || color) && (
          <p
            className={cx('tw:mb-0 tw:text-sm tw:font-medium tw:!leading-none tw:text-neutrals-medium-grey', {
              'tw:text-xs': compact,
            })}>
            {size && `${size} |`} {color}
          </p>
        )}
      </div>
    </div>
  );
};
